import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  Button,
  Flex,
  Icon,
  Text,
} from '@kognitos/lattice';

import { useExportAutomation } from '@/generated/hooks/grimoire/v1/import_export';
import type { V1ExportAutomationResponse } from '@/generated/hooks/grimoire/models/v1ExportAutomationResponse';
import type { AxiosResponse } from 'axios';
import {
  selectCurrentOrganizationId,
  selectCurrentWorkspaceId,
  useUserPreferencesStore,
} from '@/shared/stores/user-preferences.store';
import { errorHandler } from '@/shared/utils/error-handler';
import { captureException } from '@/modules/sentry';
import { downloadUrl, showToast } from '@/shared/utils';
import { useAutomationRevisions } from '@/modules/automation/hooks/useAutomationRevisions';
import { AutomationVersionRow } from './AutomationVersionRow';

/**
 * Props for the ExportAutomationDialog component.
 */
interface IExportAutomationDialogProps {
  /** Whether the dialog is open */
  open: boolean;
  /** Callback to close the dialog */
  onClose: () => void;
  /** Display name of the automation to export */
  title: string;
  /** ID of the automation to export */
  automationId: string;
  /** Revision id to select by default (latest published for a published
   * automation, current draft version for a draft). */
  defaultRevisionId?: string;
  /** Whether the automation is a draft (drives the status-dot color). */
  isDraft?: boolean;
  /** Optional callback invoked after a successful export */
  onExport?: () => void;
}

/** A single descriptive line within an info panel. */
interface IExportInfoItem {
  key: string;
  title: string;
  subtitle: string;
}

/**
 * Dialog for exporting an automation as a Zip bundle.
 *
 * Lets the user pick which published revision to export, explains what is and
 * isn't included in the bundle, and on confirmation calls the export API and
 * downloads the resulting Zip from the returned presigned URL.
 *
 * @param {IExportAutomationDialogProps} props - Component props
 * @returns {JSX.Element} Export automation dialog
 */
export function ExportAutomationDialog({
  open,
  onClose,
  title,
  automationId,
  defaultRevisionId,
  isDraft = false,
  onExport,
}: IExportAutomationDialogProps) {
  const { t: tAutomation } = useTranslation('automation');
  const { t: tCommon } = useTranslation('common');

  const currentOrganizationId = useUserPreferencesStore(
    selectCurrentOrganizationId,
  );
  const currentWorkspaceId = useUserPreferencesStore(selectCurrentWorkspaceId);

  // The revision explicitly chosen by the user, if any. Until they pick one we
  // derive the selection (latest published version, then newest loaded revision).
  const [userSelectedRevisionId, setUserSelectedRevisionId] = useState<
    string | undefined
  >(undefined);

  const {
    versions,
    publishedVersionIds,
    isLoading: isRevisionsLoading,
    isLoadError: isRevisionsLoadError,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    refetch: refetchRevisions,
  } = useAutomationRevisions(automationId, open);

  // Effective selection: the user's explicit choice, else the variant default
  // (latest published / current draft), else the newest loaded revision.
  const selectedRevisionId =
    userSelectedRevisionId ?? defaultRevisionId ?? versions[0] ?? '';

  const { mutateAsync: exportAutomationAsync, isPending: isExportPending } =
    useExportAutomation();

  // Reset the explicit selection on close so reopening starts from the default.
  const handleClose = useCallback(() => {
    setUserSelectedRevisionId(undefined);
    onClose();
  }, [onClose]);

  const handleExport = async () => {
    if (!selectedRevisionId) return;

    try {
      const response: AxiosResponse<V1ExportAutomationResponse> =
        await exportAutomationAsync({
          organization: currentOrganizationId,
          workspace: currentWorkspaceId,
          automation: automationId,
          data: { revision_id: selectedRevisionId },
        });
      const zipUri = response.data.zip_uri;
      if (zipUri) {
        downloadUrl(zipUri, `${title}.zip`);
        showToast(
          tAutomation('automation.exportConfirmation.success'),
          undefined,
          'success',
        );
        handleClose();
        onExport?.();
      } else {
        // A 200 with no download URL is still a failure from the user's
        // perspective: surface it rather than closing silently.
        showToast(
          tAutomation('automation.exportConfirmation.error'),
          undefined,
          'error',
        );
      }
    } catch (error) {
      errorHandler(error, () => {
        showToast(
          tAutomation('automation.exportConfirmation.error'),
          undefined,
          'error',
        );
        captureException(error as Error, {
          tags: { module: 'automation', action: 'export_automation' },
        });
      });
    }
  };

  const willExportItems: IExportInfoItem[] = [
    {
      key: 'automation',
      title: tAutomation(
        'automation.exportConfirmation.willExport.automation.title',
      ),
      subtitle: tAutomation(
        'automation.exportConfirmation.willExport.automation.subtitle',
      ),
    },
    {
      key: 'dependencies',
      title: tAutomation(
        'automation.exportConfirmation.willExport.dependencies.title',
      ),
      subtitle: tAutomation(
        'automation.exportConfirmation.willExport.dependencies.subtitle',
      ),
    },
  ];

  const willNotExportItems: IExportInfoItem[] = [
    {
      key: 'secrets',
      title: tAutomation(
        'automation.exportConfirmation.willNotExport.secrets.title',
      ),
      subtitle: tAutomation(
        'automation.exportConfirmation.willNotExport.secrets.subtitle',
      ),
    },
    {
      key: 'history',
      title: tAutomation(
        'automation.exportConfirmation.willNotExport.history.title',
      ),
      subtitle: tAutomation(
        'automation.exportConfirmation.willNotExport.history.subtitle',
      ),
    },
    {
      key: 'triggers',
      title: tAutomation(
        'automation.exportConfirmation.willNotExport.triggers.title',
      ),
      subtitle: tAutomation(
        'automation.exportConfirmation.willNotExport.triggers.subtitle',
      ),
    },
  ];

  const renderInfoItems = (items: IExportInfoItem[]) =>
    items.map(item => (
      <Flex key={item.key} direction="column" width="full">
        <Text level="small">{item.title}</Text>
        <Text level="small" color="muted">
          {item.subtitle}
        </Text>
      </Flex>
    ));

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent
        data-testid="automation-export-dialog"
        className="flex max-h-[calc(100vh-2rem)] flex-col max-w-[640px]"
        stopPropagation
      >
        <DialogHeader>
          <DialogTitle>
            {tAutomation('automation.exportConfirmation.title')}
          </DialogTitle>
          <DialogDescription>
            {tAutomation('automation.exportConfirmation.description')}
          </DialogDescription>
        </DialogHeader>

        <Flex
          direction="column"
          gap={6}
          width="full"
          className="min-h-0 flex-1 overflow-y-auto"
        >
          <AutomationVersionRow
            title={title}
            selectedRevisionId={selectedRevisionId}
            versions={versions}
            publishedVersionIds={publishedVersionIds}
            isLoading={isRevisionsLoading}
            isLoadError={isRevisionsLoadError}
            hasNextPage={hasNextPage}
            isFetchingNextPage={isFetchingNextPage}
            onSelect={setUserSelectedRevisionId}
            onLoadMore={fetchNextPage}
            onRetry={refetchRevisions}
            isDraft={isDraft}
          />

          {/* Info panels */}
          <Flex gap={5} width="full" align="stretch">
            <Flex
              direction="column"
              gap={3}
              className="flex-1 rounded-xl bg-secondary p-4"
            >
              <Flex align="center" gap={1} width="full">
                <Icon
                  type="Check"
                  size="sm"
                  className="text-success shrink-0"
                />
                <Text level="small" color="muted">
                  {tAutomation(
                    'automation.exportConfirmation.willExport.title',
                  )}
                </Text>
              </Flex>
              {renderInfoItems(willExportItems)}
            </Flex>

            <Flex
              direction="column"
              gap={3}
              className="flex-1 rounded-xl bg-secondary p-4"
            >
              <Flex align="center" gap={1} width="full">
                <Icon
                  type="X"
                  size="sm"
                  className="text-muted-foreground shrink-0"
                />
                <Text level="small" color="muted">
                  {tAutomation(
                    'automation.exportConfirmation.willNotExport.title',
                  )}
                </Text>
              </Flex>
              {renderInfoItems(willNotExportItems)}
            </Flex>
          </Flex>
        </Flex>

        <DialogFooter className="justify-between">
          <Button
            variant="outline"
            onClick={handleClose}
            data-testid="automation-export-dialog-cancel"
          >
            {tCommon('common.action.cancel')}
          </Button>
          <Button
            onClick={handleExport}
            disabled={isExportPending || !selectedRevisionId}
            isLoading={isExportPending}
            data-testid="automation-export-dialog-confirm"
          >
            {tAutomation('automation.exportConfirmation.actions.export')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
